import {useEffect, useState} from "react";
import apiClient from "../utils/apiClient";
import AdminDay from "../components/AdminDay";
import {useNavigate} from "react-router-dom";
import {THEME_OPTIONS, getStoredTheme, setTheme as persistTheme, saveThemeToServer, fetchThemeFromServer} from "../utils/themeManager";

function Admin() {
    const [canLogin, setCanLogin] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [currentTab, setCurrentTab] = useState("settings");
    const [termini, setTermini] = useState();
    const [booked, setBooked] = useState();
    const [clientPasswordError, setClientPasswordError] = useState("");
    const [clientPasswordSuccess, setClientPasswordSuccess] = useState("");
    const [adminPasswordError, setAdminPasswordError] = useState("");
    const [adminPasswordSuccess, setAdminPasswordSuccess] = useState("");
    const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(new Date());
    const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
    const [isCurrentWeek, setIsCurrentWeek] = useState(true);
    const [isLastAvailableWeek, setIsLastAvailableWeek] = useState(false);
    const [filteredTermini, setFilteredTermini] = useState([]);
    const [appointmentActionStatus, setAppointmentActionStatus] = useState("");
    const [themePreference, setThemePreference] = useState(getStoredTheme());

    const navigate = useNavigate();

    useEffect(() => {
        fetchThemeFromServer().then(theme => {
            if (theme) setThemePreference(theme);
        });
    }, []);

    function getStartOfWeek(date) {
        const newDate = new Date(date);
        const day = newDate.getDay();
        const diff = day === 0 ? -6 : (1 - day);
        newDate.setDate(newDate.getDate() + diff);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
    }

    async function handleThemeChange(event) {
        const selectedTheme = event.target.value;
        setThemePreference(selectedTheme);
        try {
            await saveThemeToServer(selectedTheme);
        } catch (error) {
            console.error("Failed to save theme", error);
        }
    }

    const formatWeekDisplay = () => {
        const startDate = new Date(currentWeekStart);
        const endDate = new Date(currentWeekStart);
        endDate.setDate(endDate.getDate() + 6);
        const startMonth = startDate.toLocaleDateString('sl-SI', { month: 'long' });
        const endMonth = endDate.toLocaleDateString('sl-SI', { month: 'long' });
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const year = endDate.getFullYear();
        if (startMonth === endMonth) {
            return `${startMonth} ${startDay} - ${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    const goToPreviousWeek = () => {
        if (!isCurrentWeek) {
            const prevWeekStart = new Date(currentWeekStart);
            prevWeekStart.setDate(prevWeekStart.getDate() - 7);
            setCurrentWeekStart(prevWeekStart);
        }
    };

    const goToNextWeek = () => {
        if (!isLastAvailableWeek) {
            const nextWeekStart = new Date(currentWeekStart);
            nextWeekStart.setDate(nextWeekStart.getDate() + 7);
            setCurrentWeekStart(nextWeekStart);
        }
    };

    const goToCurrentWeek = () => {
        setCurrentWeekStart(getStartOfWeek(new Date()));
    };
    
    const checkIfLastAvailableWeek = (allDates, weekStart) => {
        if (!allDates || allDates.length === 0) {
            setIsLastAvailableWeek(true);
            return;
        }
        const lastDate = new Date(Math.max(...allDates.map(d => new Date(d.date).getTime())));
        const lastWeekStart = getStartOfWeek(lastDate);
        setIsLastAvailableWeek(weekStart.getTime() >= lastWeekStart.getTime());
    };

    const formatSlovenianBookingDate = (dateValue) => {
        if (!dateValue) return '';
        const date = new Date(dateValue);
        const formatted = date.toLocaleDateString('sl-SI', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    async function getTermini() {
        try {
            const res = await apiClient.get("/termins");
            setTermini(res.data.data);
        } catch (error) {
            console.error("Error fetching termini:", error);
        }
    }    
    
    useEffect(() => {
        const fetchData = async () => {
            await getTermini();
        };
        fetchData();
        const fetchBookings = async () => {
            const res = await apiClient.get("/termins/admin/booked");
            setBooked(res.data);
        };
        fetchBookings();
    }, [currentWeekStart]); // Add dependency on currentWeekStart to refresh data when week changes

    useEffect(() => {
        if (!termini) return;
        const endOfWeek = new Date(currentWeekStart);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        const weekStart = new Date(currentWeekStart);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(endOfWeek);
        weekEnd.setHours(23, 59, 59, 999);
        const filtered = termini.filter(item => {
            const itemDate = new Date(item.date);
            itemDate.setHours(12, 0, 0, 0);
            return itemDate >= weekStart && itemDate <= weekEnd;
        });
        filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
        setFilteredTermini(filtered);
        const today = new Date();
        const todayWeekStart = getStartOfWeek(today);
        setIsCurrentWeek(todayWeekStart.getTime() === currentWeekStart.getTime());
        checkIfLastAvailableWeek(termini, currentWeekStart);
    }, [termini, currentWeekStart]);

    async function completeAppointment(id) {
        try {
            const res = await apiClient.put(`/termins/booking/${id}/complete`);
            if (res.status === 200) {
                setAppointmentActionStatus("Termin je bil uspešno označen kot zaključen");
                const updatedBookings = await apiClient.get("/termins/admin/booked");
                setBooked(updatedBookings.data);
                setTimeout(() => {
                    setAppointmentActionStatus("");
                }, 3000);
            }
        } catch (error) {
            setAppointmentActionStatus("Napaka pri označevanju termina kot zaključenega");
            console.error(error);
            setTimeout(() => {
                setAppointmentActionStatus("");
            }, 3000);
        }
    }
    
    async function deleteAppointment(id) {
        if (!window.confirm("Ali ste prepričani, da želite izbrisati ta termin?")) {
            return;
        }
        try {
            const res = await apiClient.delete(`/termins/booking/${id}`);
            if (res.status === 200) {
                setAppointmentActionStatus("Termin je bil uspešno izbrisan");
                const updatedBookings = await apiClient.get("/termins/admin/booked");
                setBooked(updatedBookings.data);
                setTimeout(() => {
                    setAppointmentActionStatus("");
                }, 3000);
            }
        } catch (error) {
            setAppointmentActionStatus("Napaka pri brisanju termina");
            console.error(error);
            setTimeout(() => {
                setAppointmentActionStatus("");
            }, 3000);
        }
    }

    async function refreshBookings() {
        try {
            setAppointmentActionStatus("Osveževanje...");
            const res = await apiClient.get("/termins/admin/booked");
            setBooked(res.data);
            setTimeout(() => {
                setAppointmentActionStatus("");
            }, 1000);
        } catch (error) {
            setAppointmentActionStatus("Failed to refresh bookings");
            console.error(error);
            setTimeout(() => {
                setAppointmentActionStatus("");
            }, 3000);
        }
    }

    function handleInput(e) {
        setPassword(e.target.value);
        setError("");
    }

    async function handleLogin() {
        try {
            const res = await apiClient.post("/auth/admin-password", {password});
            if (res.data.isMatch) {
                setLoggedIn(true);
                setError("");
            } else {
                setError("Napačno geslo");
            }
        } catch (error) {
            setError("Prišlo je do napake pri preverjanju gesla.");
            console.error(error);
        }
    }

    function handleKeyPress(e) {
        if (e.key === "Enter") {
            handleLogin();
        }
    }

    function logOut() {
        setLoggedIn(false);
    }

    function setTab(val) {
        setCurrentTab(val);
    }    const getAppointmentsForDate = (date) => {
        if (!booked) return [];
        return booked.filter(booking => {
            // Extract date parts from booking.date (ISO string)
            const bookingDate = new Date(booking.date);
            // Compare year, month, and day to ensure exact date match
            return bookingDate.getFullYear() === date.getFullYear() &&
                   bookingDate.getMonth() === date.getMonth() &&
                   bookingDate.getDate() === date.getDate();
        });
    }

    async function updateClientPassword(oldPassword, newPassword, confirmPassword) {
        if (newPassword !== confirmPassword) {
            setClientPasswordError("Nova gesla se ne ujemajo.");
            setClientPasswordSuccess("");
            return;
        }
        try {
            const res = await apiClient.put("/auth/client-password", {
                oldPassword,
                newPassword
            });
            if (res.status === 200) {
                setClientPasswordSuccess("Geslo stranke je bilo uspešno posodobljeno.");
                setClientPasswordError("");
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setClientPasswordError("Napačno trenutno geslo.");
            } else {
                setClientPasswordError("Napaka pri posodobitvi gesla stranke.");
            }
            setClientPasswordSuccess("");
            console.error(error);
        }
    }

    async function updateAdminPassword(oldPassword, newPassword, confirmPassword) {
        if (newPassword !== confirmPassword) {
            setAdminPasswordError("Nova gesla se ne ujemajo.");
            setAdminPasswordSuccess("");
            return;
        }
        try {
            const res = await apiClient.put("/auth/admin-password", {
                oldPassword,
                newPassword
            });
            if (res.status === 200) {
                setAdminPasswordSuccess("Skrbniško geslo je bilo uspešno posodobljeno.");
                setAdminPasswordError("");
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setAdminPasswordError("Napačno trenutno geslo.");
            } else {
                setAdminPasswordError("Napaka pri posodobitvi skrbniškega gesla.");
            }
            setAdminPasswordSuccess("");
            console.error(error);
        }
    }

    return (
        <div className="App">
            {!loggedIn ? (
                <div className="login-page" id="admin-login">
                    <div className="site-branding">
                        <div className="site-info">
                            <div className="site-title">POKOSIMO.COM</div>
                            <div className="site-subtitle">REZERVACIJE</div>
                        </div>
                    </div>
                    
                    <div className="background-overlay"></div>

                    <div className="login-content">
                        <div className="page-header">
                            <h1>SKRBNIŠKI DOSTOP</h1>
                            <p>Vnesite geslo za dostop do skrbniške nadzorne plošče</p>
                        </div>

                        <div className="login-form">
                            <div className="input-group">
                                <i className="fas fa-lock input-icon"></i>
                                <input
                                    onInput={handleInput}
                                    onKeyPress={handleKeyPress}
                                    type="password"
                                    id="admin-password"
                                    placeholder="VNESITE SKRBNIŠKO GESLO"
                                    value={password}
                                />
                            </div>
                            <div className="btn-group">
                                <button
                                    onClick={handleLogin}
                                    disabled={!password}
                                    id="admin-login-btn"
                                    className={`btn-primary ${password ? "" : "cant-login"}`}
                                >
                                    PRIJAVA <i className="fas fa-arrow-right"></i>
                                </button>
                            </div>
                            {error && <p className="error">{error}</p>}

                            <div className="client-login-link">
                                <button id="back-to-home" className="btn-secondary" onClick={() => navigate("/")}>
                                    PRIJAVA STRANKE <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (                <div className="admin-page">
                    <div className="site-branding">
                        <div className="site-info">
                            <div className="site-title">POKOSIMO.COM</div>
                            <div className="site-subtitle desktop-only">NADZORNA PLOŠČA</div>
                            <div className="site-subtitle mobile-only">NADZORNA PLOŠČA</div>
                        </div>
                    </div>
                    
                    <div className="background-overlay"></div>                    <div className="page-header desktop-only">
                        <h1>NADZORNA PLOŠČA</h1>
                        <p>Upravljanje terminov in nastavitev vaše brivnice</p>
                    </div><div className="admin-dashboard">
                        {/* Desktop Sidebar Navigation */}
                        <div className="admin-sidebar desktop-only">
                            <button className={currentTab === "settings" ? "active admin-nav-btn" : "admin-nav-btn"}
                                    data-tab="settings" onClick={() => setTab("settings")}>
                                <i className="fas fa-sliders-h"></i> NASTAVITVE
                            </button>
                            <button className={currentTab === "bookings" ? "active admin-nav-btn" : "admin-nav-btn"}
                                    data-tab="bookings" onClick={() => setTab("bookings")}>
                                <i className="fas fa-calendar-check"></i> TERMINI
                            </button>
                            <button className={currentTab === "security" ? "active admin-nav-btn" : "admin-nav-btn"}
                                    data-tab="security" onClick={() => setTab("security")}>
                                <i className="fas fa-shield-alt"></i> VARNOST
                            </button>
                            <button id="logout-admin-btn" className="admin-nav-btn logout" onClick={() => logOut()}>
                                <i className="fas fa-sign-out-alt"></i> ODJAVA
                            </button>
                        </div>

                        <div>
                            <div className={currentTab === "settings" ? "admin-tab active" : "admin-tab hidden"}>                                <div className="week-picker">
                                    <div className="week-info" id="admin-bookings-week-info">
                                        {formatWeekDisplay()}
                                        {isCurrentWeek && (
                                            <span className="current-week-indicator">
                                                (Trenutni)
                                            </span>
                                        )}
                                    </div>
                                    <div className="week-nav-buttons">
                                        <button 
                                            className="week-nav-btn"
                                            onClick={goToPreviousWeek} 
                                            disabled={isCurrentWeek}
                                            title="Prejšnji teden"
                                            aria-label="Prejšnji teden"
                                        >
                                            &lt;
                                        </button>
                                        <button 
                                            className="week-nav-btn"
                                            onClick={goToNextWeek} 
                                            disabled={isLastAvailableWeek}
                                            title="Naslednji teden"
                                            aria-label="Naslednji teden"
                                        >
                                            &gt;
                                        </button>
                                    </div>
                                </div><div className="admin-card">
                                    <h2>
                                        <i className="fas fa-calendar-alt"></i> 
                                        <span className="desktop-only">DELOVNI ČAS</span>
                                        <span className="mobile-only">URE</span>
                                    </h2>
                                    <div id="admin-bookings-container" className="bookings-list">
                                        {filteredTermini && filteredTermini.length > 0 ? (
                                            filteredTermini.map((termin) => <AdminDay key={termin.id} data={termin}/>)
                                        ) : (
                                            <div className="empty-state">Za ta teden ni konfiguriranih datumov. Za navigacijo uporabite kontrolnike zgoraj.</div>
                                        )}
                                    </div>
                                </div>
                            </div>                            <div className={currentTab === "bookings" ? "admin-tab active" : "admin-tab hidden"}>                                <div className="date-picker-card">
                                    <div className="day-selector">
                                        <button 
                                            className="day-nav-btn"
                                            onClick={() => {
                                                const newDate = new Date(selectedAppointmentDate);
                                                newDate.setDate(newDate.getDate() - 1);
                                                setSelectedAppointmentDate(newDate);
                                            }}
                                            disabled={(() => {
                                                const today = new Date();
                                                const oneWeekAgo = new Date();
                                                oneWeekAgo.setDate(today.getDate() - 7);
                                                return selectedAppointmentDate <= oneWeekAgo;
                                            })()}
                                            aria-label="Prejšnji dan"
                                        >
                                            &lt;
                                        </button>
                                        <div className="selected-date">                                            <span className="date">
                                                {window.innerWidth <= 480 ? 
                                                    selectedAppointmentDate.toLocaleDateString('sl-SI', { 
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })
                                                    :
                                                    selectedAppointmentDate.toLocaleDateString('sl-SI', { 
                                                        weekday: 'long',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                            </span>
                                            {selectedAppointmentDate.toDateString() === new Date().toDateString() && (
                                                <span className="today-indicator">(Danes)</span>
                                            )}
                                        </div>
                                        <button 
                                            className="day-nav-btn"
                                            onClick={() => {
                                                const newDate = new Date(selectedAppointmentDate);
                                                newDate.setDate(newDate.getDate() + 1);
                                                setSelectedAppointmentDate(newDate);
                                            }}
                                            aria-label="Naslednji dan"
                                        >
                                            &gt;
                                        </button>
                                    </div>
                                </div>
                                <div className="admin-card">
                                    <div className="appointments-header">
                                        <h2>REZERVACIJE</h2>
                                        <button className="admin-action-btn refresh-btn" onClick={refreshBookings}>
                                            OSVEŽI
                                        </button>
                                    </div>
                                    {appointmentActionStatus && <p className={appointmentActionStatus.includes("successfully") ? "success" : "error"}>{appointmentActionStatus}</p>}
                                      {(() => {
                                        const filteredBookings = getAppointmentsForDate(selectedAppointmentDate);
                                        return filteredBookings && filteredBookings.length > 0 ? (
                                            filteredBookings.map((booking) => (
                                                <div key={`${booking.date}-${booking.time}`} className={`booking-item ${booking.completed ? 'completed' : ''}`}>                                                    <div className="booking-info">
                                                        <p><strong>Datum:</strong> {formatSlovenianBookingDate(booking.date)}</p>
                                                        <p><strong>Ura:</strong> {booking.time}</p>
                                                        <p><strong>Ime in Priimek:</strong> {booking.firstName} {booking.lastName}</p>
                                                        <p><strong>Telefon:</strong> {booking.phone}</p>
                                                        {booking.comment && (
                                                            <p><strong>Komentar:</strong> {booking.comment}</p>
                                                        )}
                                                        {booking.completed && (
                                                            <p className="completed-status"><i className="fas fa-check-circle"></i> Zaključeno</p>
                                                        )}
                                                    </div>
                                                    <div className="booking-actions">
                                                        {!booking.completed && (
                                                            <button className="admin-action-btn complete-btn" onClick={() => completeAppointment(booking.id)}>
                                                                <i className="fas fa-check"></i> ZAKLJUČI
                                                            </button>
                                                        )}
                                                        <button className="admin-action-btn delete-btn" onClick={() => deleteAppointment(booking.id)}>
                                                            <i className="fas fa-trash"></i> IZBRIŠI
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="empty-state">Za izbrani datum ni rezervacij.</div>
                                        );
                                    })()}
                                </div>
                            </div>

                            <div className={currentTab === "security" ? "admin-tab active" : "admin-tab hidden"}>
                                <div className="admin-card">
                                    <h2><i className="fas fa-lock"></i> NASTAVITVE DOSTOPA</h2>

                                    <div className="security-settings">
                                        <div className="security-section">
                                            <h3>GESLO STRANKE</h3>
                                            <p className="section-desc">Spremenite geslo za dostop do rezervacij</p>

                                            <div className="form-group">
                                                <label htmlFor="current-client-password">TRENUTNO GESLO</label>
                                                <input type="password" id="current-client-password"
                                                       placeholder="TRENUTNO GESLO"/>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="new-client-password">NOVO GESLO</label>
                                                <input type="password" id="new-client-password"
                                                       placeholder="NOVO GESLO"/>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="confirm-client-password">POTRDITE GESLO</label>
                                                <input type="password" id="confirm-client-password"
                                                       placeholder="POTRDITE GESLO"/>
                                            </div>

                                            <p className="error" id="client-password-error">{clientPasswordError}</p>
                                            <p className="success"
                                               id="client-password-success">{clientPasswordSuccess}</p>

                                            <button id="change-client-password-btn" className="admin-action-btn"
                                                    onClick={() => {
                                                        const oldPassword = document.getElementById("current-client-password").value;
                                                        const newPassword = document.getElementById("new-client-password").value;
                                                        const confirmPassword = document.getElementById("confirm-client-password").value;
                                                        updateClientPassword(oldPassword, newPassword, confirmPassword);
                                                    }}>
                                                <i className="fas fa-key"></i> POSODOBI GESLO
                                            </button>
                                        </div>

                                        <div className="security-section">
                                            <h3>SKRBNIŠKO GESLO</h3>
                                            <p className="section-desc">Spremenite skrbniško geslo za nadzorno ploščo</p> <br/>

                                            <div className="form-group">
                                                <label htmlFor="current-admin-password">TRENUTNO GESLO</label>
                                                <input type="password" id="current-admin-password"
                                                       placeholder="TRENUTNO GESLO"/>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="new-admin-password">NOVO GESLO</label>
                                                <input type="password" id="new-admin-password"
                                                       placeholder="NOVO GESLO"/>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="confirm-admin-password">POTRDITE GESLO</label>
                                                <input type="password" id="confirm-admin-password"
                                                       placeholder="POTRDITE GESLO"/>
                                            </div>

                                            <p className="error" id="admin-password-error">{adminPasswordError}</p>
                                            <p className="success"
                                               id="admin-password-success">{adminPasswordSuccess}</p>

                                            <button id="change-admin-password-btn" className="admin-action-btn"
                                                    onClick={() => {
                                                        const oldPassword = document.getElementById("current-admin-password").value;
                                                        const newPassword = document.getElementById("new-admin-password").value;
                                                        const confirmPassword = document.getElementById("confirm-admin-password").value;
                                                        updateAdminPassword(oldPassword, newPassword, confirmPassword);
                                                    }}>
                                                <i className="fas fa-key"></i> POSODOBI GESLO
                                            </button>
                                        </div>
                                        <div className="security-section">
                                            <h3>TEMA VMESNIKA</h3>
                                            <p className="section-desc">Ročno izberite barvno shemo, ki bo uporabljena na celotni strani.</p>
                                            <div className="theme-options">
                                                {THEME_OPTIONS.map((option) => {
                                                    const swatchStyle = option.value === "white"
                                                        ? {
                                                            background: "linear-gradient(135deg, #ffffff 50%, #000000 50%)"
                                                        }
                                                        : {backgroundColor: option.accent};
                                                    return (
                                                        <label
                                                            key={option.value}
                                                            className={`theme-option ${themePreference === option.value ? "selected" : ""}`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="theme"
                                                                value={option.value}
                                                                checked={themePreference === option.value}
                                                                onChange={handleThemeChange}
                                                            />
                                                            <span className="theme-swatch" style={swatchStyle}></span>
                                                            <span className="theme-label">{option.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>                        {/* Mobile Bottom Navigation Bar */}
                        <div className="admin-bottom-nav mobile-only">
                            <button 
                                className={currentTab === "settings" ? "active admin-bottom-nav-btn" : "admin-bottom-nav-btn"}
                                data-tab="settings" 
                                onClick={() => setTab("settings")}
                                aria-label="Nastavitve"
                                title="Nastavitve"
                            >
                                <i className="fas fa-sliders-h"></i>
                            </button>
                            <button 
                                className={currentTab === "bookings" ? "active admin-bottom-nav-btn" : "admin-bottom-nav-btn"}
                                data-tab="bookings" 
                                onClick={() => setTab("bookings")}
                                aria-label="Termini"
                                title="Termini"
                            >
                                <i className="fas fa-calendar-check"></i>
                            </button>
                            <button 
                                className={currentTab === "security" ? "active admin-bottom-nav-btn" : "admin-bottom-nav-btn"}
                                data-tab="security" 
                                onClick={() => setTab("security")}
                                aria-label="Varnost"
                                title="Varnost"
                            >
                                <i className="fas fa-shield-alt"></i>
                            </button>
                            <button 
                                className="admin-bottom-nav-btn"
                                onClick={() => logOut()}
                                aria-label="Odjava"
                                title="Odjava"
                            >
                                <i className="fas fa-sign-out-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Admin;